/**
 * LocationHeroStrip
 *
 * Compact hero strip shown on all inner (non-home) pages, replacing the
 * verbose "Connecting People, Preserving Memories…" tagline banner.
 *
 * Left column (call-to-action):
 *   - Big two-line "Support Social Pulse Global Initiatives · Make a Donation"
 *     heading — the primary conversion goal.
 *   - "Browse all sections" + "Submit a Cause" buttons kept visible above the fold.
 *
 * Right column (graphic):
 *   - A colourful, procedurally-tinted panel that shows the current place
 *     (city) or topic (section) name in bold display type. Each location /
 *     topic gets its own gradient via a deterministic hash so no two
 *     places look the same.
 */
import { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HeartHandshake, ArrowRight } from "lucide-react";
import BrowseAllMenu from "@/components/BrowseAllMenu";
import { useLocality } from "@/contexts/LocalityContext";
import PlaceStamp from "@/components/PlaceStamp";
import PlaceKindTag from "@/components/PlaceKindTag";

/**
 * Deterministic hash → integer.  Small djb2 variant, plenty good for
 * choosing a palette out of a small pool.
 */
const hash = (str = "") => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

/**
 * Pool of soft, editorial gradients + accent colours.  Colours are muted
 * (light-tone / pastel) so the hero panel stays readable without
 * overwhelming the page below.  Each entry is a full "look" package —
 * background gradient + accent + text colour.
 */
const PALETTES = [
  { bg: "linear-gradient(135deg,#FEE9D7 0%,#FDE0A7 60%,#F58220 100%)", accent: "#F58220", text: "#4A2506", chip: "#FFF5E5" },
  { bg: "linear-gradient(135deg,#E5EEFC 0%,#C7DBFA 60%,#1E64C8 100%)", accent: "#1E64C8", text: "#0B234F", chip: "#EEF3FE" },
  { bg: "linear-gradient(135deg,#F1E8FA 0%,#DCC6EF 60%,#7B4A9C 100%)", accent: "#7B4A9C", text: "#2B143E", chip: "#F6EFFB" },
  { bg: "linear-gradient(135deg,#E4F5EF 0%,#B7E4D0 60%,#0E7C86 100%)", accent: "#0E7C86", text: "#0B3A3F", chip: "#EEF9F5" },
  { bg: "linear-gradient(135deg,#FDE9EE 0%,#F8C5D0 60%,#B2365B 100%)", accent: "#B2365B", text: "#3E0F1F", chip: "#FBECEF" },
  { bg: "linear-gradient(135deg,#FBF3D9 0%,#F5E29E 60%,#B25D38 100%)", accent: "#B25D38", text: "#3B1D0D", chip: "#FDF6E3" },
  { bg: "linear-gradient(135deg,#E3F0F7 0%,#B6D9EB 60%,#2A6F97 100%)", accent: "#2A6F97", text: "#0F2C40", chip: "#EAF3F8" },
  { bg: "linear-gradient(135deg,#F4EED8 0%,#DFD299 60%,#7A6E2E 100%)", accent: "#7A6E2E", text: "#2E280C", chip: "#F6F1DE" },
];

const pickPalette = (seed) => PALETTES[hash(seed) % PALETTES.length];

/**
 * A tiny curated "Did you know?" fact per section so the hero panel
 * feels informative instead of purely decorative. Falls back to a
 * friendly local-community prompt on /l/... pages.
 */
const SECTION_FACTS = {
  nature:      "Our atlas holds 224+ verified species profiles — plants, birds, mammals, fungi.",
  weather:     "Reports flip to Verified once 3 neighbours confirm them.",
  travel:      "Every city page carries a live pulse, weather and open causes.",
  jobs:        "Free posting for every non-profit and community org.",
  causes:      "100% of every donation reaches the cause — no platform fees.",
  volunteer:   "Log hours, earn Social Capital, unlock verified badges.",
  marketplace: "Local buys and sells stay in-community — no long-distance shipping.",
  "real-estate": "Verified listings from owners and neighbourhood members only.",
  business:    "Bronze → Platinum verification helps neighbours find real, trusted shops.",
  businesses:  "Bronze → Platinum verification helps neighbours find real, trusted shops.",
  education:   "Scholarships, tutors and study groups — all searchable by city.",
  health:      "Community-verified clinics, camps and free health drives.",
  government:  "Public services searchable by city, with contact info kept fresh.",
  sports:      "Local teams, tryouts and pickup games — all pulled by locality.",
  arts:        "Awards, exhibitions and marketplaces for creators — city by city.",
  celebrate:   "Milestones, memorials and life stories — a permanent archive.",
  events:      "Wikipedia-sourced festivals + community submissions, deduped.",
  news:        "Local headlines cross-referenced with 3+ open sources.",
  articles:    "Every article gets a discussion box and a shareable card.",
  "social-capital": "Every act of kindness earns permanent, transferable capital.",
};

const LOCATION_FACTS = [
  "Weather, causes, jobs and neighbours — all filtered to this place.",
  "Every pulse posted here surfaces in the neighbourhood live feed.",
  "Local businesses can claim their listing free — verified badges available.",
  "Add a pulse, event or cause — takes 30 seconds and helps your neighbours.",
];

/**
 * Map a route path to a friendly topic label + subtitle used when we
 * have no location (or when a topic is more informative than the city
 * name — e.g. on "/nature" we prefer to say "Nature Pulse" rather than
 * repeating "Portland").
 */
const routeToTopic = (pathname) => {
  const m = pathname.match(/^\/([^/?#]+)/);
  const first = m ? m[1] : "";
  const dict = {
    nature: { topic: "Nature Pulse", tag: "Species · Habitats · Atlas" },
    weather: { topic: "Weather Almanac", tag: "Local reports · Alerts" },
    travel: { topic: "Travel", tag: "Places · Journeys" },
    jobs: { topic: "Jobs", tag: "Careers · Opportunities" },
    causes: { topic: "Causes", tag: "Help & be helped" },
    volunteer: { topic: "Volunteer", tag: "Give time · Make impact" },
    marketplace: { topic: "Marketplace", tag: "Local trade" },
    "real-estate": { topic: "Real Estate", tag: "Homes · Rentals" },
    business: { topic: "Business", tag: "Local commerce" },
    businesses: { topic: "Business", tag: "Local commerce" },
    education: { topic: "Education", tag: "Learn · Grow" },
    health: { topic: "Health", tag: "Care · Wellness" },
    government: { topic: "Government", tag: "Civic · Services" },
    sports: { topic: "Sports", tag: "Teams · Community" },
    arts: { topic: "Arts & Cinema", tag: "Creativity · Culture" },
    "social-capital": { topic: "Social Capital", tag: "Community trust" },
    celebrate: { topic: "Celebrate a Life", tag: "Milestones · Memories" },
    news: { topic: "News", tag: "Local pulse" },
    articles: { topic: "Articles", tag: "Stories · Explainers" },
    events: { topic: "Events", tag: "Happening near you" },
    causes_home: { topic: "Causes", tag: "Help & be helped" },
    l: { topic: "Places", tag: "Explore" },
  };
  return dict[first] || null;
};

export const LocationHeroStrip = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { locality } = useLocality();

  const topic = routeToTopic(pathname);
  const city = locality?.city;

  // On /l/<country>/<state>/<city> pages we prefer the city from the URL
  // (that's the location the user is actively browsing) over the user's
  // set locality — otherwise every location page would show the same
  // "your home town" label regardless of what the visitor is looking at.
  const locMatch = pathname.match(/^\/l\/([^/]+)(?:\/([^/]+))?(?:\/([^/?#]+))?/);
  const slugToTitle = (s) => (s || "").replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Priority for the big display type — extended to recognise
  // location-adjacent routes (`/lt/...` for a topic subpage,
  // `/places/...` for the POI directory / detail). In all three
  // cases the URL carries the city slug so the hero panel should
  // reflect the location being browsed, not the user's set
  // locality.
  const ltMatch     = pathname.match(/^\/lt\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?\/topic\//);
  const placesMatch = pathname.match(/^\/places\/(?:detail\/)?([^/]+)(?:\/([^/]+))?(?:\/([^/?#]+))?/);
  const contextMatch = locMatch || ltMatch || placesMatch;
  const onLocationContext = Boolean(contextMatch);
  // Rebuild the slug that always points at the location's MAIN
  // article (`/l/<country>/<state>/<city>`), independent of whether
  // we're currently on a topic page or a places directory.
  const contextSlug = contextMatch
    ? [contextMatch[1], contextMatch[2], contextMatch[3]].filter(Boolean).join("/")
    : "";
  const ctxCity    = contextMatch ? slugToTitle(contextMatch[3] || contextMatch[2] || contextMatch[1]) : "";
  const ctxRegion  = contextMatch?.[2] ? slugToTitle(contextMatch[2]) : "";
  const ctxCountry = contextMatch?.[1] ? slugToTitle(contextMatch[1]) : "";

  // Infer the location kind from the slug shape so we can pick the
  // right decorative stamp on the hero (state / county / city…).
  const lastSeg = (contextMatch?.[3] || contextMatch?.[2] || contextMatch?.[1] || "").toLowerCase();
  const ctxKind = (() => {
    if (!contextMatch) return null;
    if (/-county$|-parish$|-borough$/.test(lastSeg)) return "county";
    const depth = [contextMatch[1], contextMatch[2], contextMatch[3]].filter(Boolean).length;
    if (depth === 1) return "country";
    if (depth === 2) return "state";
    return "city";
  })();
  const bigLabel = onLocationContext
    ? ctxCity || city || "Explore"
    : topic?.topic || city || "Social Pulse";
  const subLabel = onLocationContext
    ? [ctxRegion, ctxCountry].filter(Boolean).join(" · ") || "Local pulse"
    : topic?.tag || (city ? [locality?.state, locality?.country].filter(Boolean).join(" · ") : "Community · Kindness");

  const palette = useMemo(() => pickPalette(`${bigLabel}|${subLabel}`), [bigLabel, subLabel]);

  // Fact chip content — informative "Did you know?" line inside the
  // hero panel. Sections have curated facts; locations rotate from a
  // small pool keyed off the city name so each city gets the same
  // fact every render (stable, no jitter) but different cities cover
  // the whole pool.
  const firstSeg = pathname.match(/^\/([^/?#]+)/)?.[1] || "";
  // Route the coloured hero panel to the location's MAIN article on
  // every location-context page (/l/... article, /lt/... topic,
  // /places/... POI listing). Fall back to the top-level section
  // page (e.g. /nature) on non-location routes.
  const chipHref = onLocationContext
    ? `/l/${contextSlug}`
    : firstSeg ? `/${firstSeg}` : "/";
  const factLine = useMemo(() => {
    if (SECTION_FACTS[firstSeg]) return SECTION_FACTS[firstSeg];
    if (onLocationContext) return LOCATION_FACTS[hash(bigLabel) % LOCATION_FACTS.length];
    return null;
  }, [firstSeg, onLocationContext, bigLabel]);

  return (
    <section
      data-testid="location-hero-strip"
      aria-label="Support Social Pulse and explore this place"
      className="relative w-full overflow-hidden border-b"
      style={{ background: "var(--wofy-surface)", borderColor: "var(--wofy-border)" }}
    >
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-6 md:py-8">
        <div className="grid md:grid-cols-5 gap-6 items-stretch">
          {/* ---------- LEFT (call-to-action) ---------- */}
          <div className="md:col-span-3 flex flex-col justify-center">
            <p
              data-testid="hero-support-heading"
              className="font-display leading-[1.2] tracking-tight text-lg sm:text-xl md:text-2xl"
            >
              <span style={{ color: "var(--wofy-primary)" }}>Support Social Pulse</span>
              <br />
              <span
                style={{
                  background: "linear-gradient(90deg, var(--wofy-secondary), #B25D38, var(--wofy-accent))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Global Initiatives — Make a Donation
              </span>
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <Link
                to="/donate"
                data-testid="hero-donate-btn"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap shadow-sm"
                style={{ background: "var(--wofy-primary)" }}
              >
                <HeartHandshake className="w-4 h-4" /> Donate
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <BrowseAllMenu />
              <Link
                to="/submit-cause"
                data-testid="hero-submit-cause"
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap shadow-sm"
                style={{ background: "var(--wofy-secondary)" }}
              >
                Submit a Cause / Plea
              </Link>
            </div>
          </div>

          {/* ---------- RIGHT (place / topic graphic panel) ---------- */}
          {/* The panel is clickable-to-drill (via a keyboard/mouse
              handler using useNavigate) rather than an outer <Link>.
              An outer <Link> would render an <a> that swallows every
              inner <Link> (nested <a> tags are invalid HTML) — that
              was the bug that made "Explore Oregon Counties/Cities/
              All About" behave as if the whole card was one link. */}
          <div
            role="link"
            tabIndex={0}
            onClick={(e) => {
              // Ignore clicks that originated on real interactive
              // children (inner Links, buttons). Those get their own
              // navigation via React Router; the wrapper only fires
              // when the user clicks empty space on the card.
              const t = e.target;
              if (t.closest && t.closest("a, button, [role='link']") !== e.currentTarget) return;
              navigate(chipHref);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(chipHref);
              }
            }}
            className="md:col-span-2 group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded-2xl"
            data-testid="hero-place-panel-link"
            aria-label={onLocationContext ? `Open ${bigLabel} article` : `Go to ${bigLabel} home`}
          >
            <div
              data-testid="hero-place-panel"
              className="relative h-full min-h-[130px] rounded-2xl overflow-hidden border p-5 flex flex-col justify-between transition-transform group-hover:-translate-y-0.5 group-hover:shadow-lg"
              style={{ background: palette.bg, borderColor: palette.accent }}
            >
              {/* Decorative blurred blobs — soft, on-brand */}
              <span aria-hidden className="pointer-events-none absolute -top-8 -right-8 w-28 h-28 rounded-full blur-2xl opacity-40" style={{ background: palette.accent }} />
              <span aria-hidden className="pointer-events-none absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-3xl opacity-30" style={{ background: palette.accent }} />

              <div className="relative flex items-start gap-4">
                <div className="flex-1 min-w-0">
                <span
                  className="inline-block text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-full"
                  style={{ background: palette.chip, color: palette.accent }}
                  data-testid="hero-place-chip"
                >
                  {onLocationContext ? "You're viewing" : "Section"}
                </span>
                <h2
                  data-testid="hero-place-name"
                  className="mt-2 font-display leading-none tracking-tight text-4xl sm:text-5xl md:text-6xl group-hover:underline decoration-2 underline-offset-4"
                  style={{ color: palette.text }}
                >
                  {bigLabel}
                </h2>
                <p
                  className="mt-2 text-base font-medium opacity-90"
                  style={{ color: palette.text }}
                  data-testid="hero-place-sub"
                >
                  {subLabel}
                </p>
                {onLocationContext && ctxKind && (
                  <div className="mt-2">
                    <PlaceKindTag
                      kind={ctxKind}
                      slug={contextSlug}
                      testid="hero-place-kind-tag"
                      asLink
                    />
                  </div>
                )}
                {factLine && (
                  <p
                    data-testid="hero-place-fact"
                    className="mt-3 text-sm leading-snug opacity-85 max-w-[36ch]"
                    style={{ color: palette.text }}
                  >
                    <span className="font-mono uppercase tracking-widest mr-1 text-xs" style={{ color: palette.accent }}>
                      Did you know?
                    </span>
                    {factLine}
                  </p>
                )}
                </div>

                {/* Decorative place stamp — deterministic per slug so
                    every place gets the same illustration on every
                    visit. Only shown on real location pages. */}
                {onLocationContext && ctxKind && (
                  <PlaceStamp
                    kind={ctxKind}
                    slug={contextSlug}
                    size={88}
                    className="shadow-md ring-2 ring-white/70"
                    title={bigLabel}
                    testid="hero-place-stamp"
                    caption
                    linkCaption
                    autoFetch
                  />
                )}
              </div>

              {/* State/country quick-links strip — surfaces the three
                  drill-down CTAs directly on the hero panel so users
                  can jump to Counties / Cities / All About without
                  scrolling down to the StatePlaces section. Only
                  shown on state pages where those children exist. */}
              {onLocationContext && (ctxKind === "state" || ctxKind === "country") && (
                <div className="relative mt-4 flex flex-wrap gap-1.5" data-testid="hero-quick-links">
                  <Link
                    to={`/l/${contextSlug}#state-counties`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[11px] font-mono uppercase tracking-widest rounded-full px-2.5 py-1 border-2 bg-white/80 hover:bg-white transition"
                    style={{ borderColor: palette.accent, color: palette.text }}
                    data-testid="hero-cta-counties"
                  >
                    Explore {ctxCity} Counties →
                  </Link>
                  <Link
                    to={`/l/${contextSlug}#state-cities`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[11px] font-mono uppercase tracking-widest rounded-full px-2.5 py-1 border-2 bg-white/80 hover:bg-white transition"
                    style={{ borderColor: palette.accent, color: palette.text }}
                    data-testid="hero-cta-cities"
                  >
                    Explore {ctxCity} Cities →
                  </Link>
                  <Link
                    to={`/lt/${contextSlug}/topic/overview`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[11px] font-mono uppercase tracking-widest rounded-full px-2.5 py-1 border-2 bg-white/80 hover:bg-white transition"
                    style={{ borderColor: palette.accent, color: palette.text }}
                    data-testid="hero-cta-about"
                  >
                    All about {ctxCity} →
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
